import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { adminService } from '../../../services/adminService';
import { realtimeService } from '../../../services/realtimeService';

const HelperManagement = () => {
  const navigate = useNavigate();
  const [helpers, setHelpers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [workloadFilter, setWorkloadFilter] = useState('all');

  useEffect(() => {
    loadHelpers();

    // Subscribe to real-time helpers changes
    const subscription = realtimeService?.subscribeToHelpers((payload) => {
      const { eventType, new: newRecord, old: oldRecord } = payload;

      if (eventType === 'INSERT') {
        // Add new helper to the list
        const newHelper = {
          id: newRecord?.id,
          name: newRecord?.name,
          avatar: newRecord?.profile_image,
          avatarAlt: newRecord?.profile_image_alt,
          type: newRecord?.type_label,
          specialties: newRecord?.expertise,
          verificationStatus: newRecord?.verification_status,
          credentialExpiry: newRecord?.credential_expiry,
          sessionsThisMonth: newRecord?.sessions_this_month,
          avgRating: newRecord?.avg_rating,
          workloadStatus: newRecord?.workload_status,
          lastActive: newRecord?.last_active
        };
        setHelpers((prev) => [newHelper, ...prev]);
      } else if (eventType === 'UPDATE') {
        // Update existing helper
        setHelpers((prev) =>
          prev?.map((helper) =>
            helper?.id === newRecord?.id
              ? {
                  ...helper,
                  name: newRecord?.name,
                  avatar: newRecord?.profile_image,
                  avatarAlt: newRecord?.profile_image_alt,
                  type: newRecord?.type_label,
                  specialties: newRecord?.expertise,
                  verificationStatus: newRecord?.verification_status,
                  credentialExpiry: newRecord?.credential_expiry,
                  sessionsThisMonth: newRecord?.sessions_this_month,
                  avgRating: newRecord?.avg_rating,
                  workloadStatus: newRecord?.workload_status,
                  lastActive: newRecord?.last_active
                }
              : helper
          )
        );
      } else if (eventType === 'DELETE') {
        // Remove deleted helper
        setHelpers((prev) => prev?.filter((helper) => helper?.id !== oldRecord?.id));
      }
    });

    // Cleanup subscription on unmount
    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const loadHelpers = async () => {
    setLoading(true);
    setError('');
    
    try {
      const { data, error: fetchError } = await adminService?.getAllHelpers();
      
      if (fetchError) {
        setError(fetchError?.message || 'Failed to load helpers');
        return;
      }
      
      setHelpers(data || []);
    } catch (err) {
      setError(err?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationChange = async (helperId, newStatus) => {
    try {
      const { error: updateError } = await adminService?.updateHelperVerification(helperId, newStatus);
      
      if (updateError) {
        alert(`Failed to update verification: ${updateError?.message}`);
        return;
      }
      
      // Reload helpers
      loadHelpers();
    } catch (err) {
      alert(`Failed to update verification: ${err?.message}`);
    }
  };

  const statusOptions = [
  { value: 'all', label: 'All Status' },
  { value: 'pending', label: 'Pending Verification' },
  { value: 'verified', label: 'Verified' },
  { value: 'at_risk', label: 'Burnout Risk' }];


  const getVerificationBadge = (status) => {
    const badges = {
      verified: { color: "bg-success/10 text-success", icon: "CheckCircle2" },
      pending: { color: "bg-warning/10 text-warning", icon: "Clock" },
      rejected: { color: "bg-error/10 text-error", icon: "XCircle" }
    };
    return badges?.[status] || badges?.pending;
  };

  const getWorkloadBadge = (status) => {
    const badges = {
      healthy: { color: "bg-success/10 text-success", label: "Healthy" },
      moderate: { color: "bg-warning/10 text-warning", label: "Moderate" },
      at_risk: { color: "bg-error/10 text-error", label: "At Risk" }
    };
    return badges?.[status] || badges?.healthy;
  };

  const isCredentialExpiring = (expiryDate) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const now = new Date();
    const daysUntilExpiry = Math.floor((expiry - now) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30;
  };

  const filteredHelpers = helpers?.filter((helper) => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'pending') return helper?.verificationStatus === 'pending';
    if (statusFilter === 'verified') return helper?.verificationStatus === 'verified';
    if (statusFilter === 'at_risk') return helper?.workloadStatus === 'at_risk';
    return true;
  });

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-gray-600">Loading helpers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="text-center py-12">
          <p className="text-error mb-4">{error}</p>
          <button 
            onClick={loadHelpers}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Icon name="UserCog" size={24} className="text-primary" />
            <div>
              <h3 className="text-lg font-semibold">Helper Management</h3>
              <p className="text-sm text-muted-foreground">{filteredHelpers?.length} helpers found</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select
              placeholder="Filter by status"
              options={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
              className="w-full sm:w-48" />

            <Button
              variant="default"
              iconName="UserPlus"
              iconPosition="left"
              onClick={() => navigate('/helper-profile-management')}>
              Add Helper
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          {filteredHelpers?.map((helper) => {
            const verificationBadge = getVerificationBadge(helper?.verificationStatus);
            const workloadBadge = getWorkloadBadge(helper?.workloadStatus);
            const credentialExpiring = isCredentialExpiring(helper?.credentialExpiry);

            return (
              <div key={helper?.id} className="border border-border rounded-lg p-6 hover:bg-muted/50 transition-colors">
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  <img
                    src={helper?.avatar}
                    alt={helper?.avatarAlt}
                    className="w-20 h-20 rounded-full object-cover"
                    onError={(e) => e.target.src = '/assets/images/no_image.png'} />


                  <div className="flex-1 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="text-lg font-semibold">{helper?.name}</h4>
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${verificationBadge?.color}`}>
                            <Icon name={verificationBadge?.icon} size={14} />
                            {helper?.verificationStatus?.charAt(0)?.toUpperCase() + helper?.verificationStatus?.slice(1)}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">{helper?.type}</p>
                        <div className="flex flex-wrap gap-2">
                          {helper?.specialties?.map((specialty, idx) =>
                          <span key={idx} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                              {specialty}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          iconName="Edit" 
                          iconPosition="left"
                          onClick={() => navigate('/helper-profile-management', { state: { helperId: helper?.id } })}>
                          Edit
                        </Button>
                        <Button variant="ghost" size="icon" iconName="MoreVertical" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Sessions This Month</p>
                        <p className="text-lg font-semibold text-data">{helper?.sessionsThisMonth}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Average Rating</p>
                        <div className="flex items-center gap-1.5">
                          <Icon name="Star" size={16} className="text-warning fill-warning" />
                          <p className="text-lg font-semibold text-data">{helper?.avgRating}</p>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Workload Status</p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${workloadBadge?.color}`}>
                          {workloadBadge?.label}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Credential Expiry</p>
                        {helper?.credentialExpiry ?
                        <div className="flex items-center gap-1.5">
                            <p className={`text-sm font-medium ${credentialExpiring ? 'text-warning' : 'text-foreground'}`}>
                              {new Date(helper.credentialExpiry)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </p>
                            {credentialExpiring &&
                          <Icon name="AlertTriangle" size={16} className="text-warning" />
                          }
                          </div> :

                        <p className="text-sm text-muted-foreground">N/A</p>
                        }
                      </div>
                    </div>

                    {helper?.verificationStatus === 'pending' &&
                    <div className="flex items-center gap-3 pt-4 border-t border-border">
                        <Button
                        variant="default"
                        size="sm"
                        iconName="CheckCircle2"
                        iconPosition="left"
                        onClick={() => handleVerificationChange(helper?.id, 'verified')}>
                          Approve
                        </Button>
                        <Button
                        variant="destructive"
                        size="sm"
                        iconName="XCircle"
                        iconPosition="left"
                        onClick={() => handleVerificationChange(helper?.id, 'rejected')}>
                          Reject
                        </Button>
                        <Button
                        variant="ghost"
                        size="sm"
                        iconName="FileText"
                        iconPosition="left"
                        onClick={() => navigate('/helper-profile-management', { state: { helperId: helper?.id, tab: 'credentials' } })}>
                          View Documents
                        </Button>
                      </div>
                    }

                    {helper?.workloadStatus === 'at_risk' &&
                    <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-warning mb-1">Burnout Risk Detected</p>
                            <p className="text-sm text-muted-foreground">
                              This helper has exceeded recommended session limits. Consider reducing their workload or scheduling a check-in.
                            </p>
                          </div>
                          <Button variant="outline" size="sm">
                            Take Action
                          </Button>
                        </div>
                      </div>
                    }
                  </div>
                </div>
              </div>);

          })}
        </div>
      </div>
    </div>);

};

export default HelperManagement;